import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoIcon from '@mui/icons-material/Info';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const PositionCalculator = ({ showIntroFromParent = false, tradingSettings = { market: 'A股', commissionRate: 0.03, stampDutyRate: 0.1, platformFee: 0, minFee: 0 } }) => {
  const theme = useTheme();
  
  // 通用手续费计算函数
  const calculateTradingFees = (amount, shares, isBuy = true) => {
    const market = tradingSettings.market || 'A股';
    let commission = 0;
    let stampDuty = 0;
    let totalFee = 0;
    
    if (market === 'A股') {
      // A股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = isBuy ? 0 : amount * (tradingSettings.stampDutyRate / 100); // 买入不收印花税
      totalFee = commission + stampDuty;
    } else if (market === '港股') {
      // 港股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = amount * (tradingSettings.stampDutyRate / 100); // 买卖都收印花税
      totalFee = commission + stampDuty;
    } else if (market === '美股') {
      // 美股费率计算
      const perShareFee = shares * tradingSettings.platformFee;
      totalFee = Math.max(perShareFee, tradingSettings.minFee);
      commission = totalFee; // 美股没有单独的佣金和印花税概念
      stampDuty = 0;
    }
    
    return {
      commission: commission,
      stampDuty: stampDuty,
      totalFee: totalFee
    };
  };
  
  // 状态变量
  const [showIntro, setShowIntro] = useState(true); // 默认展开策略介绍
  
  // 监听父组件传入的showIntroFromParent变化
  useEffect(() => {
    if (showIntroFromParent) {
      setShowIntro(true);
      // 等待DOM更新后滚动到说明部分
      setTimeout(() => {
        const element = document.getElementById('position-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [showIntroFromParent]);
  
  // 组件初始化时自动计算结果
  useEffect(() => {
    calculatePosition();
  }, []); // 空依赖数组，只在组件挂载时执行一次
  
  const [stockTrend, setStockTrend] = useState('up'); // 'up' 或 'down'
  const [operationType, setOperationType] = useState('build'); // 'build' 或 'clear'
  const [positionRhythm, setPositionRhythm] = useState('smallToBig'); // 'bigToSmall', 'smallToBig', 或 'equal'
  
  // 基本参数
  const [sharesPerLot, setSharesPerLot] = useState(100); // 每手股数
  const [totalCapital, setTotalCapital] = useState(100000); // 总操作资金
  const [startPrice, setStartPrice] = useState(10); // 开始建仓/清仓价格
  const [endPrice, setEndPrice] = useState(12); // 结束建仓/清仓价格
  const [targetPrice, setTargetPrice] = useState(15); // 目标价格（计算目标利润）
  const [totalShares, setTotalShares] = useState(10000); // 总持有股数（清仓时使用）
  const [holdingCost, setHoldingCost] = useState(8); // 持有成本（清仓时计算盈亏）
  
  // 计算结果
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // 12种策略配置
  const strategies = {
    1: { trend: 'up', operation: 'build', rhythm: 'bigToSmall', name: '趋势突破追涨法', recommendation: '强烈推荐-不适合新手' },
    2: { trend: 'up', operation: 'build', rhythm: 'smallToBig', name: '趋势确认加码法', recommendation: '强烈推荐' },
    3: { trend: 'up', operation: 'build', rhythm: 'equal', name: '趋势均分建仓法', recommendation: '可接受' },
    4: { trend: 'up', operation: 'clear', rhythm: 'bigToSmall', name: '急速止盈法', recommendation: '不推荐' },
    5: { trend: 'up', operation: 'clear', rhythm: 'smallToBig', name: '加速止盈法', recommendation: '强烈推荐' },
    6: { trend: 'up', operation: 'clear', rhythm: 'equal', name: '均分止盈法', recommendation: '可接受' },
    7: { trend: 'down', operation: 'build', rhythm: 'bigToSmall', name: '左侧抄底法（高风险）', recommendation: '极度不推荐 或 绝对禁止' },
    8: { trend: 'down', operation: 'build', rhythm: 'smallToBig', name: '左侧金字塔补仓法', recommendation: '推荐-仅适用于高手' },
    9: { trend: 'down', operation: 'build', rhythm: 'equal', name: '左侧均分抄底法', recommendation: '不推荐' },
    10: { trend: 'down', operation: 'clear', rhythm: 'bigToSmall', name: '恐慌斩仓法', recommendation: '强烈推荐' },
    11: { trend: 'down', operation: 'clear', rhythm: 'smallToBig', name: '递减止损法', recommendation: '极度不推荐 或 绝对禁止' },
    12: { trend: 'down', operation: 'clear', rhythm: 'equal', name: '均分止损法', recommendation: '可接受' }
  };
  
  // 获取当前策略
  const getCurrentStrategy = () => {
    for (const [id, strategy] of Object.entries(strategies)) {
      if (strategy.trend === stockTrend && 
          strategy.operation === operationType && 
          strategy.rhythm === positionRhythm) {
        return { id: parseInt(id), ...strategy };
      }
    }
    return null;
  };
  
  // 根据推荐级别获取颜色
  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case '强烈推荐':
        return theme.palette.error.dark; // 深红色
      case '强烈推荐-不适合新手':
        return theme.palette.error.dark; // 深红色，与强烈推荐相同
      case '推荐':
        return theme.palette.error.main; // 红色
      case '推荐-仅适用于高手':
        return theme.palette.primary.main; // 蓝色
      case '可接受':
        return theme.palette.warning.main; // 橙色
      case '不推荐':
        return theme.palette.success.light; // 浅绿色
      case '极度不推荐':
      case '极度不推荐 或 绝对禁止':
        return theme.palette.success.dark; // 深绿色
      default:
        return theme.palette.text.primary;
    }
  };
  
  // 格式化数字显示
  const formatNumber = (num) => {
    return new Intl.NumberFormat('zh-CN').format(num);
  };
  
  // 获取价格变化颜色
  const getPriceColor = (value, isProfit = false) => {
    if (isProfit) {
      return value >= 0 ? theme.palette.error.main : theme.palette.success.main;
    }
    return theme.palette.text.primary;
  };
  
  // 计算仓位
  const calculatePosition = () => {
    // 验证输入
    if ((operationType === 'build' && totalCapital <= 0) || 
        (operationType === 'clear' && totalShares <= 0) ||
        startPrice <= 0 || endPrice <= 0 || sharesPerLot <= 0) {
      alert('请输入有效的数值');
      return;
    }
    
    // 确定操作次数和比例
    let operationCount, ratios;
    if (positionRhythm === 'bigToSmall') {
      operationCount = 4;
      ratios = [0.4, 0.3, 0.2, 0.1]; // 4:3:2:1
    } else if (positionRhythm === 'smallToBig') {
      operationCount = 4;
      ratios = [0.1, 0.2, 0.3, 0.4]; // 1:2:3:4
    } else { // equal
      operationCount = 3;
      ratios = [1/3, 1/3, 1/3]; // 均分
    }
    
    // 计算价格步长
    const priceStep = (endPrice - startPrice) / (operationCount - 1);
    
    const positionResults = [];
    let totalInvestment = 0;
    let totalSharesTraded = 0;
    let totalCommission = 0;
    let totalStampDuty = 0;
    let totalCost = 0;
    let totalSellValue = 0;
    let totalNetValue = 0;
    
    // 执行操作
    for (let i = 0; i < operationCount; i++) {
      const currentPrice = startPrice + priceStep * i;
      
      if (operationType === 'build') {
        // 建仓操作
        const levelCapital = totalCapital * ratios[i];
        const lotCount = Math.floor(levelCapital / (currentPrice * sharesPerLot));
        const shares = lotCount * sharesPerLot;
        const investment = shares * currentPrice;
        
        // 计算手续费
        const fees = calculateTradingFees(investment, shares, true);
        const commission = fees.commission;
        const stampDuty = fees.stampDuty;
        const totalLevelCost = investment + fees.totalFee;
        
        totalSharesTraded += shares;
        totalInvestment += investment;
        totalCommission += commission;
        totalStampDuty += stampDuty;
        totalCost += totalLevelCost;
        
        positionResults.push({
          level: i + 1,
          price: currentPrice.toFixed(2),
          shares: shares,
          investment: investment.toFixed(2),
          commission: commission.toFixed(2),
          stampDuty: stampDuty.toFixed(2),
          totalCost: totalLevelCost.toFixed(2),
          formattedShares: formatNumber(shares),
          formattedInvestment: formatNumber(investment.toFixed(2)),
          formattedTotalCost: formatNumber(totalLevelCost.toFixed(2))
        });
      } else {
        // 清仓操作
        let sharesToSell;
        if (i === operationCount - 1) {
          // 最后一次操作：卖出所有剩余股数
          sharesToSell = totalShares - totalSharesTraded;
        } else {
          // 前面几次操作：按比例计算，但要确保是手数的整数倍
          sharesToSell = Math.floor(totalShares * ratios[i] / sharesPerLot) * sharesPerLot;
        }
        
        const sellValue = sharesToSell * currentPrice;
        
        // 计算手续费
        const fees = calculateTradingFees(sellValue, sharesToSell, false);
        const commission = fees.commission;
        const stampDuty = fees.stampDuty;
        const netSellValue = sellValue - fees.totalFee;
        
        totalSharesTraded += sharesToSell;
        totalSellValue += sellValue;
        totalCommission += commission;
        totalStampDuty += stampDuty;
        totalNetValue += netSellValue;
        
        positionResults.push({
          level: i + 1,
          price: currentPrice.toFixed(2),
          sharesToSell: sharesToSell,
          sellValue: sellValue.toFixed(2),
          commission: commission.toFixed(2),
          stampDuty: stampDuty.toFixed(2),
          netSellValue: netSellValue.toFixed(2),
          formattedSharesToSell: formatNumber(sharesToSell),
          formattedSellValue: formatNumber(sellValue.toFixed(2)),
          formattedNetSellValue: formatNumber(netSellValue.toFixed(2))
        });
      }
    }
    
    setResults(positionResults);
    
    // 设置汇总信息
    if (operationType === 'build') {
      const averageCost = totalCost / totalSharesTraded;
      const potentialValue = totalSharesTraded * targetPrice;
      const potentialCommission = potentialValue * (tradingSettings.commissionRate / 100);
      const potentialStampDuty = potentialValue * (tradingSettings.stampDutyRate / 100);
      const potentialNetValue = potentialValue - potentialCommission - potentialStampDuty;
      const potentialProfit = potentialNetValue - totalCost;
      const potentialProfitRate = (potentialProfit / totalCost) * 100;
      
      setSummary({
        totalShares: totalSharesTraded,
        totalInvestment: totalInvestment.toFixed(2),
        totalCommission: totalCommission.toFixed(2),
        totalStampDuty: totalStampDuty.toFixed(2),
        totalFees: (totalCommission + totalStampDuty).toFixed(2),
        totalCost: totalCost.toFixed(2),
        averageCost: averageCost.toFixed(2),
        potentialProfit: potentialProfit.toFixed(2),
        potentialProfitRate: potentialProfitRate.toFixed(2),
        formattedTotalShares: formatNumber(totalSharesTraded),
        formattedTotalInvestment: formatNumber(totalInvestment.toFixed(2)),
        formattedTotalCommission: formatNumber(totalCommission.toFixed(2)),
        formattedTotalStampDuty: formatNumber(totalStampDuty.toFixed(2)),
        formattedTotalFees: formatNumber((totalCommission + totalStampDuty).toFixed(2)),
        formattedTotalCost: formatNumber(totalCost.toFixed(2)),
        formattedAverageCost: formatNumber(averageCost.toFixed(2)),
        formattedPotentialProfit: formatNumber(potentialProfit.toFixed(2))
      });
    } else {
      // 清仓模式：计算盈亏
      const totalHoldingCost = totalSharesTraded * holdingCost;
      const profit = totalNetValue - totalHoldingCost;
      const profitRate = (profit / totalHoldingCost) * 100;
      const remainingShares = totalShares - totalSharesTraded;
      const averageSellPrice = totalSellValue / totalSharesTraded;
      
      setSummary({
        totalSharesToSell: totalSharesTraded,
        totalSellValue: totalSellValue.toFixed(2),
        totalCommission: totalCommission.toFixed(2),
        totalStampDuty: totalStampDuty.toFixed(2),
        totalFees: (totalCommission + totalStampDuty).toFixed(2),
        totalNetValue: totalNetValue.toFixed(2),
        totalHoldingCost: totalHoldingCost.toFixed(2),
        profit: profit.toFixed(2),
        profitRate: profitRate.toFixed(2),
        remainingShares: remainingShares,
        averageSellPrice: averageSellPrice.toFixed(2),
        formattedTotalSharesToSell: formatNumber(totalSharesTraded),
        formattedTotalSellValue: formatNumber(totalSellValue.toFixed(2)),
        formattedTotalCommission: formatNumber(totalCommission.toFixed(2)),
        formattedTotalStampDuty: formatNumber(totalStampDuty.toFixed(2)),
        formattedTotalFees: formatNumber((totalCommission + totalStampDuty).toFixed(2)),
        formattedTotalNetValue: formatNumber(totalNetValue.toFixed(2)),
        formattedTotalHoldingCost: formatNumber(totalHoldingCost.toFixed(2)),
        formattedProfit: formatNumber(profit.toFixed(2)),
        formattedRemainingShares: formatNumber(remainingShares),
        formattedAverageSellPrice: formatNumber(averageSellPrice.toFixed(2))
      });
    }
  };
  
  // 重置表单
  const resetForm = () => {
    setStockTrend('up');
    setOperationType('build');
    setPositionRhythm('smallToBig');
    setSharesPerLot(100);
    setTotalCapital(100000);
    setStartPrice(10);
    setEndPrice(12);
    setTargetPrice(15);
    setTotalShares(10000);
    setHoldingCost(8);
    setResults([]);
    setSummary(null);
  };
  
  // 切换介绍显示
  const toggleIntro = () => {
    const newShowIntro = !showIntro;
    setShowIntro(newShowIntro);
    
    // 如果是显示说明，则滚动到说明部分
    if (newShowIntro) {
      setTimeout(() => {
        const element = document.getElementById('position-intro');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  };
  
  const currentStrategy = getCurrentStrategy();
  
  return (
    <Box>
      <Grid container spacing={3}>
        {/* 计算器表单 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalculateIcon sx={{ mr: 1 }} />
              仓位管理计算器
              <Tooltip title="查看12种仓位管理策略介绍">
                <IconButton size="small" onClick={toggleIntro} sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {/* 当前策略显示 */}
            {currentStrategy && (
              <Card sx={{ 
                mb: 3, 
                bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.grey[700]}` : 'none'
              }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0 }}>
                      组合{currentStrategy.id}：{currentStrategy.name}
                    </Typography>
                    <Chip 
                      label={currentStrategy.recommendation}
                      size="small"
                      sx={{ 
                        bgcolor: getRecommendationColor(currentStrategy.recommendation),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
            
            <Grid container spacing={2}>
              {/* 股价趋势 */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>股价趋势 (A)</InputLabel>
                  <Select
                    value={stockTrend}
                    label="股价趋势 (A)"
                    onChange={(e) => setStockTrend(e.target.value)}
                  >
                    <MenuItem value="up">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                        向上
                      </Box>
                    </MenuItem>
                    <MenuItem value="down">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingDownIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                        向下
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* 操作动作 */}
              <Grid item xs={12} sm={5.2}>
                <FormControl fullWidth size="small">
                  <InputLabel>操作 (B)</InputLabel>
                  <Select
                    value={operationType}
                    label="操作 (B)"
                    onChange={(e) => setOperationType(e.target.value)}
                  >
                    <MenuItem value="build">建仓</MenuItem>
                    <MenuItem value="clear">清仓</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* 仓位节奏 */}
              <Grid item xs={12} sm={2.8}>
                <FormControl fullWidth size="small">
                  <InputLabel>仓位节奏 (C)</InputLabel>
                  <Select
                    value={positionRhythm}
                    label="仓位节奏 (C)"
                    onChange={(e) => setPositionRhythm(e.target.value)}
                  >
                    <MenuItem value="bigToSmall">先大后小 (4次)</MenuItem>
                    <MenuItem value="smallToBig">先小后大 (4次)</MenuItem>
                    <MenuItem value="equal">先后一致 (3次)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              {/* 基本参数 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="每手股数"
                  type="number"
                  fullWidth
                  value={sharesPerLot}
                  onChange={(e) => setSharesPerLot(Number(e.target.value))}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              {/* 建仓模式字段 */}
              {operationType === 'build' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="总操作资金"
                      type="number"
                      fullWidth
                      value={totalCapital}
                      onChange={(e) => setTotalCapital(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="开始建仓价格"
                      type="number"
                      fullWidth
                      value={startPrice}
                      onChange={(e) => setStartPrice(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="结束建仓价格"
                      type="number"
                      fullWidth
                      value={endPrice}
                      onChange={(e) => setEndPrice(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  {/* 目标价格和按钮在同一行 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="目标价格（计算目标利润）"
                      type="number"
                      fullWidth
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 2, height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={calculatePosition}
                        startIcon={<CalculateIcon />}
                        sx={{ flex: 1, height: '40px' }}
                      >
                        计算
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        startIcon={<RestartAltIcon />}
                        sx={{ height: '40px' }}
                      >
                        重置
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
              
              {/* 清仓模式字段 */}
              {operationType === 'clear' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="总持有股数"
                      type="number"
                      fullWidth
                      value={totalShares}
                      onChange={(e) => setTotalShares(Number(e.target.value))}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="持有成本"
                      type="number"
                      fullWidth
                      value={holdingCost}
                      onChange={(e) => setHoldingCost(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="开始清仓价格"
                      type="number"
                      fullWidth
                      value={startPrice}
                      onChange={(e) => setStartPrice(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  {/* 结束清仓价格和按钮在同一行 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="结束清仓价格"
                      type="number"
                      fullWidth
                      value={endPrice}
                      onChange={(e) => setEndPrice(Number(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 2, height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={calculatePosition}
                        startIcon={<CalculateIcon />}
                        sx={{ flex: 1, height: '40px' }}
                      >
                        计算
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        startIcon={<RestartAltIcon />}
                        sx={{ height: '40px' }}
                      >
                        重置
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
        
        {/* 计算结果 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              计算结果
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {/* 汇总信息 */}
            {summary && (
              <Card sx={{ 
                mb: 3, 
                bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.grey[700]}` : 'none'
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    汇总信息
                  </Typography>
                  <Grid container spacing={2}>
                    {operationType === 'build' ? (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">总投入资金: ¥{summary.formattedTotalInvestment}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总持仓数量: {summary.formattedTotalShares}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">平均成本价: ¥{summary.formattedAverageCost}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总佣金: ¥{summary.formattedTotalCommission}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总印花税: ¥{summary.formattedTotalStampDuty}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总手续费: ¥{summary.formattedTotalFees}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color={Number(summary.potentialProfit) >= 0 ? 'error.main' : 'success.main'}>
                            目标价盈亏: ¥{summary.formattedPotentialProfit}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color={Number(summary.potentialProfitRate) >= 0 ? 'error.main' : 'success.main'}>
                            目标价收益率: {summary.potentialProfitRate}%
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">总卖出金额: ¥{summary.formattedTotalSellValue}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总卖出数量: {summary.formattedTotalSharesToSell}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">平均卖出价: ¥{summary.formattedAverageSellPrice}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">剩余持仓: {summary.formattedRemainingShares}股</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总佣金: ¥{summary.formattedTotalCommission}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总印花税: ¥{summary.formattedTotalStampDuty}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">总手续费: ¥{summary.formattedTotalFees}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color={Number(summary.profit) >= 0 ? 'error.main' : 'success.main'}>
                            盈亏: ¥{summary.formattedProfit}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color={Number(summary.profitRate) >= 0 ? 'error.main' : 'success.main'}>
                            收益率: {summary.profitRate}%
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
            
            {results.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>层级</TableCell>
                      {operationType === 'build' ? (
                        <>
                          <TableCell>价格</TableCell>
                          <TableCell>投入资金</TableCell>
                          <TableCell>买入数量</TableCell>
          
                          <TableCell>累计成本</TableCell>
                          <TableCell>平均成本</TableCell>
                          <TableCell>预期收益</TableCell>
                          <TableCell>收益率</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>价格</TableCell>
                          <TableCell>卖出数量</TableCell>
          
                          <TableCell>卖出金额</TableCell>
                          <TableCell>累计卖出</TableCell>
                          <TableCell>剩余股数</TableCell>
                          <TableCell>级别收益</TableCell>
                          <TableCell>收益率</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row, index) => {
                      let cumulativeShares = 0;
                      let cumulativeInvestment = 0;
                      let cumulativeSold = 0;
                      
                      // 计算累计值
                      for (let i = 0; i <= index; i++) {
                        if (operationType === 'build') {
                          cumulativeShares += results[i].shares;
                          cumulativeInvestment += parseFloat(results[i].totalCost);
                        } else {
                          cumulativeSold += results[i].sharesToSell;
                        }
                      }
                      
                      const averagePrice = operationType === 'build' ? (cumulativeInvestment / cumulativeShares) : 0;
                      const expectedProfit = operationType === 'build' ? (cumulativeShares * targetPrice - cumulativeInvestment) : 0;
                      const expectedProfitRate = operationType === 'build' ? ((expectedProfit / cumulativeInvestment) * 100) : 0;
                      const levelProfit = operationType === 'clear' ? (parseFloat(row.netSellValue) - row.sharesToSell * holdingCost) : 0;
                      const levelProfitRate = operationType === 'clear' ? ((levelProfit / (row.sharesToSell * holdingCost)) * 100) : 0;
                      const remainingShares = operationType === 'clear' ? (totalShares - cumulativeSold) : 0;
                      
                      return (
                        <TableRow key={row.level}>
                          <TableCell>{row.level}</TableCell>
                          {operationType === 'build' ? (
                            <>
                              <TableCell>¥{row.price}</TableCell>
                              <TableCell>¥{row.formattedTotalCost}</TableCell>
                              <TableCell>{row.formattedShares}股</TableCell>
                              <TableCell>¥{formatNumber(cumulativeInvestment.toFixed(2))}</TableCell>
                              <TableCell>¥{formatNumber(averagePrice.toFixed(2))}</TableCell>
                              <TableCell sx={{ color: expectedProfit >= 0 ? 'error.main' : 'success.main' }}>
                                ¥{formatNumber(expectedProfit.toFixed(2))}
                              </TableCell>
                              <TableCell sx={{ color: expectedProfitRate >= 0 ? 'error.main' : 'success.main' }}>
                                {expectedProfitRate.toFixed(2)}%
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>¥{row.price}</TableCell>
                              <TableCell>{row.formattedSharesToSell}股</TableCell>
                              <TableCell>¥{row.formattedSellValue}</TableCell>
                              <TableCell>{formatNumber(cumulativeSold)}股</TableCell>
                              <TableCell>{formatNumber(remainingShares)}股</TableCell>
                              <TableCell sx={{ color: levelProfit >= 0 ? 'error.main' : 'success.main' }}>
                                ¥{formatNumber(levelProfit.toFixed(2))}
                              </TableCell>
                              <TableCell sx={{ color: levelProfitRate >= 0 ? 'error.main' : 'success.main' }}>
                                {levelProfitRate.toFixed(2)}%
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                请填写参数并点击计算按钮查看结果
              </Alert>
            )}
          </Paper>
        </Grid>
        
        {/* 策略介绍 */}
        {showIntro && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }} id="position-intro">
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 1 }} />
                12种仓位管理策略详解
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                12种策略组合一览表
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>组合编号</strong></TableCell>
                      <TableCell><strong>股价趋势</strong></TableCell>
                      <TableCell><strong>操作动作</strong></TableCell>
                      <TableCell><strong>仓位节奏</strong></TableCell>
                      <TableCell><strong>策略名称</strong></TableCell>
                      <TableCell><strong>推荐等级</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(strategies).map(([id, strategy]) => (
                      <TableRow key={id}>
                        <TableCell>{id}</TableCell>
                        <TableCell>
                          {strategy.trend === 'up' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TrendingUpIcon sx={{ mr: 0.5, color: 'error.main' }} />
                              向上
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TrendingDownIcon sx={{ mr: 0.5, color: 'success.main' }} />
                              向下
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{strategy.operation === 'build' ? '建仓' : '清仓'}</TableCell>
                        <TableCell>
                          {strategy.rhythm === 'bigToSmall' ? '先大后小' : 
                           strategy.rhythm === 'smallToBig' ? '先小后大' : '先后一致'}
                        </TableCell>
                        <TableCell>{strategy.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={strategy.recommendation}
                            size="small"
                            sx={{ 
                              bgcolor: getRecommendationColor(strategy.recommendation),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                策略组合说明
              </Typography>
              <Typography variant="body2" paragraph>
                本计算器基于股票仓位管理的3个核心变量组合，形成12种不同的仓位管理策略：
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li><strong>变量A - 股价趋势</strong>：向上（上涨趋势）、向下（下跌趋势）</li>
                  <li><strong>变量B - 操作动作</strong>：建仓（买入开仓）、清仓（卖出平仓）</li>
                  <li><strong>变量C - 仓位节奏</strong>：先大后小（递减）、先小后大（递增）、先后一致（均分）</li>
                </ul>
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                推荐策略等级
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: theme.palette.error.dark, color: 'white' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold">
                        强烈推荐
                      </Typography>
                      <Typography variant="body2">
                        • 组合1：趋势突破追涨法
                      </Typography>
                      <Typography variant="caption">
                        上涨+建仓+先大后小
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        • 组合2：趋势确认加码法
                      </Typography>
                      <Typography variant="caption">
                        上涨+建仓+先小后大
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        • 组合5：加速止盈法
                      </Typography>
                      <Typography variant="caption">
                        上涨+清仓+先小后大
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        • 组合10：恐慌斩仓法
                      </Typography>
                      <Typography variant="caption">
                        下跌+清仓+先大后小
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: theme.palette.error.main, color: 'white' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold">
                        推荐
                      </Typography>
                      <Typography variant="body2">
                        • 组合8：金字塔补仓法
                      </Typography>
                      <Typography variant="caption">
                        下跌+建仓+先小后大
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: theme.palette.warning.main, color: 'white' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold">
                        可接受
                      </Typography>
                      <Typography variant="body2">
                        • 组合3：趋势均分建仓法
                      </Typography>
                      <Typography variant="body2">
                        • 组合6：均分止盈法
                      </Typography>
                      <Typography variant="body2">
                        • 组合12：均分止损法
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: theme.palette.success.dark, color: 'white' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold">
                        极度不推荐
                      </Typography>
                      <Typography variant="body2">
                        • 组合7：左侧抄底法
                      </Typography>
                      <Typography variant="caption">
                        下跌+建仓+先大后小
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        • 组合11：递减止损法
                      </Typography>
                      <Typography variant="caption">
                        下跌+清仓+先小后大
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                策略详细分析
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                   组合1 - 趋势突破追涨法（强烈推荐-不适合新手）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   在突破关键位置时重仓入场，力求快速获取大部分利润。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>效率极高，如果判断正确，收益会非常可观，能够快速抓住突破行情的主要利润。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>风险极高，需要极强的趋势判断能力和快速的止损纪律，一旦判断错误损失巨大。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>仅适合有丰富经验的投资者，新手绝对不要使用。必须设置严格的止损位，并严格执行。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                   组合2 - 趋势确认加码法（强烈推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   经典且稳健的趋势跟随策略。先小仓位试探，待趋势得到确认后再逐步加码。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>风险可控，初始亏损小，能够在趋势确认后获得较好收益，是"试探-加码"原则的经典应用。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>可能错过趋势初期的部分利润，需要一定的趋势判断能力。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>最适合大多数趋势投资者的建仓方法，建议作为主要建仓策略使用。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="info.main">
                   组合3 - 趋势均分建仓法（可接受）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   机械式分步建仓，不判断趋势强度。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>简单易懂，完全规避情绪影响，能平滑持仓成本，操作纪律性强。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>无法根据趋势强度调整仓位，可能在弱趋势中建仓过多，在强趋势中建仓不足。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>适合追求稳健、不希望频繁决策的投资者，可作为辅助策略使用。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                   组合4 - 急速止盈法（不推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   在预期趋势可能见顶时，先卖出大部分仓位锁定利润。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>能够快速锁定利润，避免利润大幅回撤，在震荡市中表现较好。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>如果趋势未结束，会过早失去大部分头寸，错过后期主要利润，容易"卖飞"。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>不推荐作为主要止盈策略，仅在明确判断趋势即将结束时谨慎使用。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                   组合5 - 加速止盈法（强烈推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   "让利润奔跑"的经典止盈策略。趋势初期只卖出少量仓位，随着股价不断上涨时逐步加大卖出力度。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>既能锁定基础利润，又能最大限度抓住主升浪，完美平衡了风险控制和利润最大化。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>需要对趋势有较好的判断能力，在震荡市中可能频繁操作。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>强烈推荐作为主要止盈策略，特别适合趋势性行情。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="info.main">
                   组合6 - 均分止盈法（可接受）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   机械式分步止盈，到达预设目标位就卖出等量仓位。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>策略简单，纪律性强，能获得相对平均的止盈价格，操作规范化。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>无法根据趋势强度调整卖出节奏，可能在强趋势中过早卖出过多。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>适合有明确目标收益率的投资者，可作为稳健的止盈策略。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                   组合7 - 左侧抄底法（极度不推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   "接飞刀"式操作，在下跌初期就重仓买入试图抄底。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>如果成功抄底，能够获得极低的成本价格和丰厚收益。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>这是最危险的策略之一，如果下跌趋势延续，会导致巨额亏损，成功概率极低。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>强烈不推荐！绝对禁止新手使用，即使是经验丰富的投资者也应避免。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                   组合8 - 左侧金字塔补仓法（推荐-仅适用于高手）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   在下跌过程中，价格越低买入仓位越大，以摊薄成本。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>如果成功抄底，成本会非常有优势，能够有效摊薄持仓成本。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>仍是左侧交易，存在继续下跌的风险，需要大量资金支持。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>仅适用于极度看好且基本面未恶化的标的，必须有严格的止损纪律和充足的资金。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                   组合9 - 左侧均分抄底法（不推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   在下跌过程中，每下跌一定幅度就买入等量仓位。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>比"先大后小"风险稍低，能够平均摊薄成本，操作相对简单。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>仍是逆势操作，容易买在半山腰，无法根据下跌程度调整买入力度。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>不推荐使用，如果一定要抄底，建议选择组合8的金字塔方式。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                   组合10 - 恐慌斩仓法（强烈推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   当下跌趋势确认或破位时，第一时间果断卖出大部分仓位。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>止损坚决，能最大限度保住本金，防止亏损扩大，是专业交易员的核心生存法则。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>可能在假突破时过早止损，错过后续反弹机会。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>强烈推荐作为主要止损策略，保命比赚钱更重要，宁可错杀不可放过。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                   组合11 - 递减止损法（极度不推荐）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   刚开始跌时只卖一点，越跌卖得越多。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>几乎没有优点，可能在小幅回调时减少不必要的止损。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>这是最糟糕的止损策略，导致"小亏不走，大亏割肉"的典型散户悲剧，风险控制效率极低。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>绝对不推荐！这是散户亏损的主要原因之一，必须坚决避免。
                 </Typography>
               </Box>
              
              <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" fontWeight="bold" color="info.main">
                   组合12 - 均分止损法（可接受）
                 </Typography>
                 <Typography variant="body2" paragraph>
                   设置多个止损位，每到一个止损位就平掉一部分仓位。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>优点：</strong>既不会因过于恐慌而全部卖飞，也不会因犹豫而死扛，是很好的平衡型止损策略。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>缺点：</strong>在急跌行情中可能止损不够及时，无法根据下跌速度调整止损力度。
                 </Typography>
                 <Typography variant="body2" paragraph>
                   <strong>建议：</strong>可作为稳健的止损策略，特别适合风险承受能力中等的投资者。
                 </Typography>
               </Box>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                操作比例说明
              </Typography>
              <Typography variant="body2" paragraph>
                • <strong>先大后小（递减）</strong>：4次操作，比例为 4:3:2:1，适用于确定性较高的场景
              </Typography>
              <Typography variant="body2" paragraph>
                • <strong>先小后大（递增）</strong>：4次操作，比例为 1:2:3:4，适用于试探性操作
              </Typography>
              <Typography variant="body2" paragraph>
                • <strong>先后一致（均分）</strong>：3次操作，每次均分，操作简单但缺乏灵活性
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                关于止损的特别说明
              </Typography>
              <Typography variant="body2" paragraph>
                止损不是12种组合中的一种独立操作，而应是一项<strong>铁律</strong>，贯穿于所有建仓策略之中。强烈建议采用"组合10：恐慌斩仓法"的模式执行止损，即一旦触发止损条件，第一时间卖出大部分（例如2/3或3/4）仓位。
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                核心建议
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>黄金组合推荐：</strong><br/>
                  • <strong>建仓策略：组合2（趋势确认加码法）</strong> - 先小后大，让市场证明你是对的再加大投入<br/>
                  • <strong>止盈策略：组合5（加速止盈法）</strong> - 先小后大，跟随趋势，让利润最大化<br/>
                  • <strong>止损策略：组合10（恐慌斩仓法）</strong> - 快速认错，保护本金<br/>
                  • <strong>高效策略：组合1（趋势突破追涨法）</strong> - 适合有经验的投资者，需要极强的判断能力
                </Typography>
              </Alert>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>重要提示：</strong>如果在操作过程中股价趋势发生明显变化（如从上涨转为下跌或从下跌转为上涨），
                  应立即停止后续操作，重新评估市场情况并调整策略。趋势判断错误是仓位管理失败的主要原因。
                </Typography>
              </Alert>
              
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>免责声明：</strong>本计算器仅为投资教育工具，所有策略和建议仅供参考，不构成投资建议。
                  股票投资存在风险，投资者应根据自身风险承受能力谨慎决策。使用本工具进行的任何投资决策及其结果，
                  均由投资者自行承担责任。过往表现不代表未来收益，投资需谨慎。
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PositionCalculator;